"use client";

/**
 * Texto que troca NO LUGAR sem semântica de desfecho: nome no cabeçalho da
 * conversa, contagem com o substantivo, rótulo de filtro, etapa no chip. A
 * face que sai sobe e desfoca, a nova pousa (a receita `rotulo`), e as duas
 * dividem a mesma célula de grade enquanto trocam.
 *
 * `largura`:
 * - "acompanha" (padrão): a caixa anda até a largura do texto novo pela mola
 *   `layout`, sem esticar o texto.
 * - "fixa": a caixa fica do tamanho que o pai der (use quando o vizinho não
 *   pode se mexer).
 *
 * `quebra`: frase que pode quebrar linha (a caixa é a largura do pai, sem
 * medir; as duas faces dividem a célula enquanto trocam).
 */
import { AnimatePresence, m } from "motion/react";
import type { CSSProperties, ReactNode } from "react";
import { useMovimento } from "./provider";

export function TrocaDeRotulo({
  chave,
  children,
  alinhar = "inicio",
  largura = "acompanha",
  quebra = false,
  className,
  style,
}: {
  /** Muda a chave, troca o texto. */
  chave: string | number;
  children: ReactNode;
  alinhar?: "inicio" | "centro" | "fim";
  largura?: "acompanha" | "fixa";
  /** Frase que pode quebrar linha: ocupa a largura do pai e não mede. */
  quebra?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const mov = useMovimento();
  const r = mov.r.rotulo;
  const justify = alinhar === "centro" ? "center" : alinhar === "fim" ? "end" : "start";
  if (quebra) {
    return (
      <span className={className} style={{ display: "grid", minWidth: 0, justifyItems: justify, ...style }}>
        <AnimatePresence initial={false}>
          <m.span
            key={String(chave)}
            initial={r.initial}
            animate={r.animate}
            exit={r.exit}
            style={{ gridArea: "1 / 1", minWidth: 0 }}
          >
            {children}
          </m.span>
        </AnimatePresence>
      </span>
    );
  }
  return (
    <m.span
      layout={largura === "acompanha" ? mov.layout : false}
      transition={{ layout: mov.mola("layout") }}
      className={className}
      style={{
        position: "relative",
        display: "inline-grid",
        justifyItems: justify,
        alignItems: "center",
        verticalAlign: "bottom",
        ...style,
      }}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <m.span
          key={String(chave)}
          layout={largura === "acompanha" ? "position" : false}
          initial={r.initial}
          animate={r.animate}
          exit={r.exit}
          style={{ gridArea: "1 / 1", whiteSpace: "nowrap" }}
        >
          {children}
        </m.span>
      </AnimatePresence>
    </m.span>
  );
}
